import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useStore, useDispatch } from "../../../store";
import {
  fetchSpecialties,
  unFollowSpecialty,
  followSpecialty,
} from "../../../store/specialties/actions";
import { FollowDialog, MainSpinner } from "../../../components";
import SpecialtiesError from "../../../components/SpecialtiesError";
import {
  Anchor,
  Flex,
  Type,
  Icon,
  Toast,
} from "discover.medical.react-component-library";
import {
  SpecialtiesTable,
  SpecialtyItemWrapper,
  SpecialtyLinkAnchor,
  SpecialtyFollowButton,
} from "./index.styled";
import {
  logBrowseItemClicked,
  logBrowseItemFollowedCanceled,
} from "../../../util/analytics";
import {
  useFollowDialogStore,
  SubscribeToNothing,
} from "../../../components/FollowDialog/FollowDialogContext";
import { useEventBus, EVENT_BUS_EVENTS } from "../../../util/useEventBus";
const affordance = "spectialties-list";

const SpecialtiesLayout = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [toast, setToast] = useState(false);
  const path = useLocation().pathname;

  const [, setFollowDialogStore] = useFollowDialogStore(SubscribeToNothing);
  const {
    isFetching,
    specialtiesList,
    hasError,
    specialtyFollowError,
  } = store.specialties;

  useEffect(() => {
    fetchSpecialties(dispatch, path);
  }, [dispatch, path]);

  const eventBus = useEventBus();
  useEffect(() => {
    const handler = () => fetchSpecialties(dispatch);
    eventBus.on(EVENT_BUS_EVENTS.SPECIALTY_UNSUBSCRIBED, handler);
    return () =>
      eventBus.detach(EVENT_BUS_EVENTS.SPECIALTY_UNSUBSCRIBED, handler);
  }, [eventBus, dispatch]);

  useEffect(() => {
    if (
      specialtyFollowError &&
      specialtyFollowError?.followError &&
      specialtyFollowError?.followError?.message
    ) {
      setToast(true);
    }
  }, [specialtyFollowError]);

  const handleToasterClose = () => {
    setToast(false);
  };

  const handleFollowIconClick = (followData) => {
    if (!followData?.functionArgs?.isFollowing) {
      if (specialtiesList?.modalData) {
        setFollowDialogStore({ isOpen: true, saveData: followData });
      } else if (followData?.functionArgs?.subscriptionType) {
        const { apiPath, feedId, specialtyId, subscriptionType } =
          followData?.functionArgs || {};
        followSpecialty(
          dispatch,
          apiPath,
          specialtyId,
          affordance,
          feedId,
          subscriptionType
        );
      } else {
        eventBus.emit(EVENT_BUS_EVENTS.LAUNCH_CTA, null, {
          type: "article-subscription-browse",
          originator: {
            mfe: "browse",
            userAction: "follow icon click",
            component: "SpecialtiesLayout",
          },
        });
      }
    } else {
      const { feedId, specialtyId } = followData?.functionArgs;
      unFollowSpecialty(dispatch, specialtyId, affordance, feedId);
    }
  };

  const handleFollowActions = (followData, subscriptionType) => {
    const { apiPath, feedId, specialtyId } = followData?.functionArgs || {};
    setFollowDialogStore({ isOpen: false });

    followSpecialty(
      dispatch,
      apiPath,
      specialtyId,
      affordance,
      feedId,
      subscriptionType
    );
  };

  const closeFollowDialog = (specialtyId) => {
    setFollowDialogStore({ isOpen: false });
    logBrowseItemFollowedCanceled(affordance, specialtyId);
  };

  const handleSpecialtyItemClick = (telemetryData) => {
    logBrowseItemClicked(telemetryData);
  };

  if (isFetching && !hasError) {
    return <MainSpinner />;
  }
  if (!isFetching && hasError) {
    return <SpecialtiesError />;
  }

  return (
    <Flex>
      <Type
        element="h1"
        fontFamily="secondary"
        color="gray800"
        paddingBottom="sm"
        data-testid="specialty-header-label"
      >
        {specialtiesList?.header?.label}
      </Type>
      <Type element="p" fontSize="sm" data-testid="specialty-description-label">
        {specialtiesList?.header?.description}

        <Anchor fontSize="sm" href={specialtiesList?.header.link?.route}>
          {specialtiesList?.header?.link?.label}
        </Anchor>
      </Type>
      <SpecialtiesTable data-testid="specialties-list">
        {specialtiesList?.specialties?.map((specialtyItem) => {
          return (
            <SpecialtyItemWrapper data-testid="specialty-item">
              {specialtyItem?.specialtyLink && (
                <SpecialtyLinkAnchor
                  data-testid="specialty-item-link"
                  href={specialtyItem?.specialtyLink?.route}
                  linkComponent={Link}
                  display="inline-block"
                  onClick={() =>
                    handleSpecialtyItemClick(specialtyItem.sharedTelemetryData)
                  }
                >
                  {specialtyItem?.specialtyLink?.label}
                </SpecialtyLinkAnchor>
              )}

              {specialtyItem?.followData &&
                Object.keys(specialtyItem?.followData).length > 0 && (
                  <SpecialtyFollowButton
                    kind="text"
                    data-testid="follow-button"
                    padded={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFollowIconClick(specialtyItem.followData);
                    }}
                  >
                    <Icon
                      icon={{
                        size: specialtyItem?.followData?.icon?.size,
                        glyph: specialtyItem?.followData?.icon?.glyph,
                        color: specialtyItem?.followData?.icon?.color,
                      }}
                    />
                    <Type color="gray800" fontSize="sm">
                      {specialtyItem?.followData?.label}
                    </Type>
                  </SpecialtyFollowButton>
                )}
            </SpecialtyItemWrapper>
          );
        })}
      </SpecialtiesTable>
      {specialtiesList?.modalData && (
        <FollowDialog
          modalData={specialtiesList.modalData}
          onRequestClose={() => {
            closeFollowDialog(
              specialtiesList.followData?.functionArgs?.specialtyId
            );
          }}
          onSave={handleFollowActions}
        />
      )}
      {specialtyFollowError &&
        specialtyFollowError?.followError &&
        specialtyFollowError?.followError?.message && (
          <Toast
            isOpen={toast}
            onClose={handleToasterClose}
            data-testid="follow-toast"
            type="error"
            children={specialtyFollowError?.followError?.message}
          />
        )}
    </Flex>
  );
};

SpecialtiesLayout.displayName = "SpecialtiesLayout";

export default SpecialtiesLayout;
